import React, { useCallback, useEffect, useState } from "react";
import { useWallTheme } from "./public/registry";
import { findManifest } from "./public/manifest";
import { widgetPalette, widgetCss } from "./../widget/public/shared/colorUtils";

// Walls are a page takeover rather than a box on someone else's site, so they
// page in as the visitor scrolls instead of loading everything at once. Twelve
// fills three or four rows of a multi-column grid before the first scroll.
const PAGE_SIZE = 12;

// Public renderer for /walls/:uuid. Pages the wall's proofs through the
// public API, picks the right theme by kind, and renders. Themes live in
// ./public/{kind}.jsx and are registered in ./public/registry.jsx.
export const WallShow = ({ initWall }) => {
  const wall = initWall.wall;
  const manifest = findManifest(wall.kind);
  // Only this theme is downloaded, not all twelve. The page emits a
  // modulepreload for it, so the chunk is usually already in flight before
  // this hook asks for it.
  const { Theme, state: themeState } = useWallTheme(wall.kind);

  const [proofs, setProofs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [nextOffset, setNextOffset] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  const fetchPage = useCallback(async (offset) => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (offset) params.set("offset", String(offset));
    // Constrain the API to the kinds this theme can render. Without it a
    // single-kind theme (Reels = video, Audio = audio) gets pages it filters
    // to nothing and the wall looks empty while more proofs sit behind it.
    if (manifest?.kinds?.length) params.set("kinds", manifest.kinds.join(","));
    const response = await fetch(`/api/v1/walls/${wall.uuid}/proofs?${params.toString()}`);
    if (!response.ok) return null;
    return response.json();
  }, [wall.uuid, manifest]);

  useEffect(() => {
    let cancelled = false;
    fetchPage(0)
      .then((data) => {
        if (cancelled) return;
        setProofs((data && data.proofs) || []);
        setNextOffset(data?.next_offset ?? null);
        if (!data || data.next_offset == null) setExhausted(true);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) { setExhausted(true); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || exhausted || nextOffset == null) return;
    setLoadingMore(true);
    const data = await fetchPage(nextOffset);
    if (data) {
      setProofs((prev) => prev.concat(data.proofs || []));
      setNextOffset(data.next_offset ?? null);
      if (data.next_offset == null) setExhausted(true);
    } else {
      // A failed page would otherwise leave the sentinel retrying forever.
      setExhausted(true);
    }
    setLoadingMore(false);
  }, [loadingMore, exhausted, nextOffset, fetchPage]);

  if (!Theme) {
    // A chunk still in flight draws nothing rather than flashing the error
    // below. Only a theme we cannot load at all gets the message.
    if (themeState === "loading") return null;

    return (
      <div className="min-h-screen flex items-center justify-center text-center px-6 text-slate-500">
        <div>
          <p className="font-black text-2xl text-slate-900">Unknown wall theme</p>
          <p className="opacity-80 text-sm mt-2">"{wall.kind}" isn't registered.</p>
        </div>
      </div>
    );
  }

  // Same rules the widgets get.
  const scopedCss = widgetCss(widgetPalette(wall));

  return (
    <div className="ss-widget-scope">
      {scopedCss && <style>{scopedCss}</style>}
      <Theme
        wall={wall}
        proofs={proofs}
        loaded={loaded}
        loadMore={loadMore}
        loadingMore={loadingMore}
        exhausted={exhausted}
      />
    </div>
  );
};

export default WallShow;
